import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, EyeOff, Phone, Mail, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COUNTRY_CODES = [
  { code: '+91', flag: '🇮🇳', name: 'India' },
  { code: '+1', flag: '🇺🇸', name: 'USA/Canada' },
  { code: '+44', flag: '🇬🇧', name: 'UK' },
  { code: '+61', flag: '🇦🇺', name: 'Australia' },
  { code: '+971', flag: '🇦🇪', name: 'UAE' },
  { code: '+65', flag: '🇸🇬', name: 'Singapore' },
  { code: '+60', flag: '🇲🇾', name: 'Malaysia' },
  { code: '+1', flag: '🇨🇦', name: 'Canada' },
  { code: '+49', flag: '🇩🇪', name: 'Germany' },
  { code: '+33', flag: '🇫🇷', name: 'France' },
  { code: '+81', flag: '🇯🇵', name: 'Japan' },
  { code: '+86', flag: '🇨🇳', name: 'China' },
  { code: '+55', flag: '🇧🇷', name: 'Brazil' },
  { code: '+27', flag: '🇿🇦', name: 'South Africa' },
  { code: '+234', flag: '🇳🇬', name: 'Nigeria' },
  { code: '+254', flag: '🇰🇪', name: 'Kenya' },
  { code: '+92', flag: '🇵🇰', name: 'Pakistan' },
  { code: '+880', flag: '🇧🇩', name: 'Bangladesh' },
  { code: '+94', flag: '🇱🇰', name: 'Sri Lanka' },
  { code: '+977', flag: '🇳🇵', name: 'Nepal' },
];

const PhoneInput = ({ countryCode, onCountryCodeChange, phone, onPhoneChange, id, placeholder }) => (
  <div className="flex gap-2">
    <select
      value={countryCode}
      onChange={(e) => onCountryCodeChange(e.target.value)}
      className="h-11 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring w-28 shrink-0"
    >
      {COUNTRY_CODES.map((c, i) => (
        <option key={`${c.code}-${c.name}`} value={c.code}>
          {c.flag} {c.code}
        </option>
      ))}
    </select>
    <Input
      id={id}
      type="tel"
      placeholder={placeholder || '9876543210'}
      value={phone}
      onChange={(e) => onPhoneChange(e.target.value.replace(/\D/g, ''))}
      className="h-11 text-base flex-1"
    />
  </div>
);

const VendorAuth = () => {
  const navigate = useNavigate();
  const { vendorLogin, vendorSignup } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [loginMode, setLoginMode] = useState('email'); // 'email' | 'phone'

  const [loginData, setLoginData] = useState({ email: '', phone: '', countryCode: '+91', password: '' });
  const [signupData, setSignupData] = useState({
    email: '', password: '', business_name: '', phone: '', countryCode: '+91',
  });

  // Forgot password flow: null | 'send' | 'verify'
  const [forgotStep, setForgotStep] = useState(null);
  const [forgotPhone, setForgotPhone] = useState('');
  const [forgotCountryCode, setForgotCountryCode] = useState('+91');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const getLoginIdentifier = () => {
    if (loginMode === 'email') return loginData.email;
    return `${loginData.countryCode}${loginData.phone}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await vendorLogin(getLoginIdentifier(), loginData.password);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const phone = signupData.phone ? `${signupData.countryCode}${signupData.phone}` : '';
      await vendorSignup(signupData.email, signupData.password, signupData.business_name, phone);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSendForgotOtp = async (e) => {
    e.preventDefault();
    if (!forgotPhone) { toast.error('Please enter your phone number'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/vendor/forgot-password`, {
        phone: `${forgotCountryCode}${forgotPhone}`,
      });
      toast.success('OTP sent to your WhatsApp');
      setForgotStep('verify');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (forgotNewPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/vendor/reset-password`, {
        phone: `${forgotCountryCode}${forgotPhone}`,
        otp: forgotOtp,
        new_password: forgotNewPassword,
      });
      toast.success('Password reset successfully! Please login.');
      setForgotStep(null);
      setForgotPhone('');
      setForgotOtp('');
      setForgotNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  const resetForgotFlow = () => {
    setForgotStep(null);
    setForgotPhone('');
    setForgotOtp('');
    setForgotNewPassword('');
    setShowForgotPassword(false);
  };

  if (forgotStep) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30 px-3 py-6 sm:px-4 sm:py-8">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="space-y-2 pb-4 sm:pb-6">
            <button
              onClick={resetForgotFlow}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-1"
            >
              <ArrowLeft className="h-4 w-4" /> Back to login
            </button>
            <CardTitle className="text-xl sm:text-2xl font-bold">Reset Password</CardTitle>
            <CardDescription>
              {forgotStep === 'send'
                ? 'Enter your registered WhatsApp number to receive an OTP.'
                : 'Enter the OTP sent to your WhatsApp and set a new password.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            {forgotStep === 'send' ? (
              <form onSubmit={handleSendForgotOtp} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">WhatsApp Number</Label>
                  <PhoneInput
                    countryCode={forgotCountryCode}
                    onCountryCodeChange={setForgotCountryCode}
                    phone={forgotPhone}
                    onPhoneChange={setForgotPhone}
                    id="forgot-phone"
                  />
                </div>
                <Button type="submit" className="w-full h-11 text-base font-semibold" disabled={loading}>
                  {loading ? 'Sending OTP...' : 'Send OTP on WhatsApp'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="forgot-otp" className="text-sm font-medium">OTP</Label>
                  <Input
                    id="forgot-otp"
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter 6-digit OTP"
                    value={forgotOtp}
                    onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    className="h-11 text-base tracking-widest"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="forgot-new-password" className="text-sm font-medium">New Password</Label>
                  <div className="relative">
                    <Input
                      id="forgot-new-password"
                      type={showForgotPassword ? 'text' : 'password'}
                      placeholder="New password (min 6 chars)"
                      value={forgotNewPassword}
                      onChange={(e) => setForgotNewPassword(e.target.value)}
                      required
                      minLength={6}
                      className="h-11 text-base pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(!showForgotPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showForgotPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full h-11 text-base font-semibold" disabled={loading}>
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
                <button
                  type="button"
                  onClick={() => setForgotStep('send')}
                  className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Resend OTP
                </button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/30 px-3 py-6 sm:px-4 sm:py-8">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-2 pb-4 sm:pb-6">
          <CardTitle className="text-xl sm:text-2xl font-bold text-center">Vendor Portal</CardTitle>
          <CardDescription className="text-center text-sm sm:text-base">
            Sign in or create your account to get started
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          <Tabs defaultValue="login">
            <TabsList className="grid w-full grid-cols-2 h-11 mb-6">
              <TabsTrigger value="login" data-testid="login-tab" className="text-sm sm:text-base font-medium">
                Login
              </TabsTrigger>
              <TabsTrigger value="signup" data-testid="signup-tab" className="text-sm sm:text-base font-medium">
                Sign Up
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-0">
              <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
                {/* Email / Phone toggle */}
                <div className="flex rounded-lg border border-input overflow-hidden h-10 mb-1">
                  <button
                    type="button"
                    onClick={() => setLoginMode('email')}
                    className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-medium transition-colors ${
                      loginMode === 'email' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Mail className="h-3.5 w-3.5" /> Email
                  </button>
                  <button
                    type="button"
                    onClick={() => setLoginMode('phone')}
                    className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-medium transition-colors ${
                      loginMode === 'phone' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Phone className="h-3.5 w-3.5" /> Phone
                  </button>
                </div>

                <div className="space-y-2">
                  {loginMode === 'email' ? (
                    <>
                      <Label htmlFor="login-email" className="text-sm font-medium">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@example.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                        data-testid="login-email-input"
                        className="h-11 text-base"
                        autoComplete="email"
                      />
                    </>
                  ) : (
                    <>
                      <Label className="text-sm font-medium">Phone Number</Label>
                      <PhoneInput
                        countryCode={loginData.countryCode}
                        onCountryCodeChange={(v) => setLoginData({ ...loginData, countryCode: v })}
                        phone={loginData.phone}
                        onPhoneChange={(v) => setLoginData({ ...loginData, phone: v })}
                        id="login-phone"
                      />
                    </>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password" className="text-sm font-medium">Password</Label>
                    <button
                      type="button"
                      onClick={() => setForgotStep('send')}
                      className="text-xs text-primary hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showLoginPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      data-testid="login-password-input"
                      className="h-11 text-base pr-10"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="login-password-toggle"
                    >
                      {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 text-base font-semibold mt-6"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup" className="mt-0">
              <form onSubmit={handleSignup} className="space-y-5" data-testid="signup-form">
                <div className="space-y-2">
                  <Label htmlFor="signup-business" className="text-sm font-medium">Business Name</Label>
                  <Input
                    id="signup-business"
                    type="text"
                    placeholder="My Awesome Store"
                    value={signupData.business_name}
                    onChange={(e) => setSignupData({ ...signupData, business_name: e.target.value })}
                    required
                    data-testid="signup-business-input"
                    className="h-11 text-base"
                    autoComplete="organization"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email" className="text-sm font-medium">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="you@example.com"
                    value={signupData.email}
                    onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                    required
                    data-testid="signup-email-input"
                    className="h-11 text-base"
                    autoComplete="email"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Phone Number <span className="text-muted-foreground font-normal">(for WhatsApp alerts)</span>
                  </Label>
                  <PhoneInput
                    countryCode={signupData.countryCode}
                    onCountryCodeChange={(v) => setSignupData({ ...signupData, countryCode: v })}
                    phone={signupData.phone}
                    onPhoneChange={(v) => setSignupData({ ...signupData, phone: v })}
                    id="signup-phone"
                    placeholder="9876543210"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password" className="text-sm font-medium">Password</Label>
                  <div className="relative">
                    <Input
                      id="signup-password"
                      type={showSignupPassword ? 'text' : 'password'}
                      placeholder="Create a strong password"
                      value={signupData.password}
                      onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                      required
                      data-testid="signup-password-input"
                      className="h-11 text-base pr-10"
                      autoComplete="new-password"
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSignupPassword(!showSignupPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="signup-password-toggle"
                    >
                      {showSignupPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Minimum 6 characters</p>
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 text-base font-semibold mt-6"
                  disabled={loading}
                  data-testid="signup-submit-btn"
                >
                  {loading ? 'Creating Account...' : 'Sign Up'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorAuth;
